import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  ChakraGrid,
  ChakraGridItem,
  FAIconName,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  Text,
} from '@equip.health/ui';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { CarePlanProblemResponse } from '~/lib/types/carePlan/carePlans';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CarePlanFrequencyTooltipIcon } from '~/components/maud/patients/profile/tabs/careplan/CarePlanFrequencyTooltipIcon';
import { NOT_YET_ASSESSED_FREQUENCY_CODES } from '~/lib/constants/carePlan.constants';

type Props = {
  problem: CarePlanProblemResponse;
  displayProblemLabels?: boolean;
  onEditProblemClick: (problem: CarePlanProblemResponse) => void;
};
export const CarePlanGoalCardProblemRow = ({
  problem,
  displayProblemLabels = true,
  onEditProblemClick,
}: Props) => {
  const { trackEvent } = useAnalytics();
  const {
    carePlanNotesDisplay: isCarePlanNotesDisplayEnabled,
    manifestationProblemStatusFrequency: isFrequencyDisplayEnabled,
  } = useFlags<LDFlags>();

  const problemMenuOptions: FireflyKebabMenuItem[] = [
    {
      label: 'Edit problem',
      leftIcon: FAIconName.penRegular,
      onClick: () => {
        trackEvent(CarePlanEvents.clickOnEditProblemKebabMenuOption);
        onEditProblemClick(problem);
      },
    },
  ];

  return (
    <ChakraGrid
      bgColor="neutral.background.secondary"
      borderRadius="12px"
      columnGap="12px"
      marginTop="18px"
      padding="12px"
      templateAreas={`"header options" "description options"`}
      templateColumns="1fr auto"
      templateRows="auto 1fr"
    >
      {displayProblemLabels && (
        <ChakraGridItem gridArea="header" marginBottom="2px">
          <Text variant="bodyMedium">{problem.pathwayProblem.label}</Text>
        </ChakraGridItem>
      )}
      <ChakraGridItem gridArea="description">
        <Text color="neutral.secondary" variant="caption">
          {problem.description}
        </Text>
      </ChakraGridItem>
      <ChakraGridItem
        alignItems="center"
        display="flex"
        gap="12px"
        gridArea="options"
      >
        {isFrequencyDisplayEnabled && problem.frequency && (
          <CarePlanFrequencyTooltipIcon
            createdByUserId={problem.frequency.createdByUserId}
            effectiveDate={problem.frequency.effectiveDate}
            frequencyLabel={problem.frequency.label}
            id={`care-plan-problem-${problem.pathwayProblem.code}`}
            label={
              problem.pathwayProblem.providerFacingLanguage ??
              problem.pathwayProblem.label
            }
            showChangeDetails={
              isCarePlanNotesDisplayEnabled &&
              !NOT_YET_ASSESSED_FREQUENCY_CODES.includes(
                problem.frequency.code,
              )
            }
          />
        )}
        <FireflyKebabMenu
          aria-label={`Options menu for ${problem.pathwayProblem.label}`}
          onMenuOpen={() => trackEvent(CarePlanEvents.clickOnProblemKebabMenu)}
          options={problemMenuOptions}
        />
      </ChakraGridItem>
    </ChakraGrid>
  );
};
